import { action, makeAutoObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { TFunction } from 'i18next';
import { AbortRequestsStore } from '../interfaces';
import ViewStore from '../ViewStore';
import { GetFormViewerResponse, SynchronizeRepositoryForWebAppResponse } from './Payloads/responses';
import { SynchronizeRepositoryForWebAppRequest } from './Payloads/requests';
import {
  hasPendingFavoriteChanges,
  mergeServerFavorites,
  normalizeFormId,
  normalizeFormIds,
  normalizeUserUPN,
  parseStoredFavorites,
  sameFormIds,
  serializeStoredFavorites,
  StoredFavorites,
} from './favorites';
import { OrganizationnalUnitCodeHeaderName } from './const';
import { FormViewer } from '90.quickConnect.Models/models';
import { mapBaseForm } from '90.quickConnect.Models/mappings';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { API_GET_FORM_LIST, API_SYNCHRONIZE_REPOSITORY_FOR_WEB_APP } from '40.quickConnect.DataAccess/axios/apiRoutes';
import buildApiUrl from '40.quickConnect.DataAccess/axios/buildApiUrl';
import RootStore from '30.quickConnect.Stores/RootStore';
import IClientHTTP from '40.quickConnect.DataAccess/ClientHTTP/interface';
import { errorHandler } from '80.quickConnect.Core/helpers';
import { LegacyFavoriteFormEntry } from '90.quickConnect.Models/models/declarations/form';
import { DeclarationSynchronizationStatus } from '90.quickConnect.Models/enums';
import { FEATURE_013 } from '30.quickConnect.Stores/RootStore/LoginStore/const';

class FormStore implements AbortRequestsStore {
  // Tag
  private static readonly TAG = '30.quickConnect.Stores/RootStore/FormStore/index.ts';

  // Favoris de l'ancien stockage navigateur, mis de côté à la construction pour être repris par le serveur
  private static readonly LEGACY_FAVORITES_KEY = 'FormStore.legacyFavorites';

  // Préfixe de la clé des favoris conservés dans le navigateur, suivi de l'UPN normalisé de l'utilisateur
  private static readonly FAVORITES_KEY_PREFIX = 'FormStore.favorites.';

  // Durée de validité du cache des listes de formulaires par UO
  private static readonly FORMS_LIST_BY_OU_TTL_MS = 5 * 60 * 1000;

  clientHTTP: IClientHTTP;
  shouldAbortRequests = false;

  numberDisplayed = 0;

  MainMenuStore: ViewStore<FormViewer>;

  LeftMenuStore: ViewStore<FormViewer>;

  formsWithSchemaId: FormViewer[] | undefined;

  /**
   * Favoris de formulaires (FEAT-013) : liste courante, identifiants normalisés.
   * Le serveur fait foi (favoris partagés avec les applis mobiles) : la liste est relue au chargement de l'écran
   * des formulaires, au retour sur l'onglet et au retour de la connexion, et remplacée par la liste fusionnée
   * après chaque envoi.
   * Elle est conservée dans le navigateur avec la référence, par utilisateur : les changements faits hors
   * connexion survivent à un rechargement de la page et sont envoyés au premier appel réussi.
   */
  favoriteFormIds: string[] = [];

  // Dernière liste reçue du serveur (référence de la fusion), undefined si jamais reçue
  private favoriteBaseFormIds: string[] | undefined;

  // Utilisateur (UPN normalisé) auquel appartiennent les listes de favoris
  private favoritesUserUPN: string | undefined;

  // Appels de synchro des favoris, l'un après l'autre : chacun part avec la référence renvoyée par le précédent
  private favoritesQueue: Promise<void> = Promise.resolve();

  // Dernier appel de synchro des favoris en échec : l'avertissement n'est affiché qu'au premier échec d'une série
  private favoritesSyncFailed = false;

  // Traduction utilisée pour reconstruire l'arbre quand les favoris changent
  private favoritesT: TFunction | undefined;

  // Cache non observable : il ne sert qu'à limiter les appels réseau
  private formsListByOuCache = new Map<string, { expiresAt: number; forms: Promise<FormViewer[] | null> }>();

  logger: CustomLogger;

  RootStore: RootStore;

  constructor(rootStore: RootStore, logger: CustomLogger) {
    this.logger = logger;
    this.RootStore = rootStore;
    this.clientHTTP = rootStore.clientHTTP;
    makeAutoObservable<
      FormStore,
      | 'formsListByOuCache'
      | 'favoriteBaseFormIds'
      | 'favoritesUserUPN'
      | 'favoritesQueue'
      | 'favoritesSyncFailed'
      | 'favoritesT'
    >(
      this,
      {
        formsListByOuCache: false,
        favoriteBaseFormIds: false,
        favoritesUserUPN: false,
        favoritesQueue: false,
        favoritesSyncFailed: false,
        favoritesT: false,
        shouldAbortRequests: observable,
        formsWithSchemaId: observable,
        favoriteFormIds: observable,

        getFormsList: action.bound,
        toggleFavorite: action.bound,
        setShouldAbortRequests: action,
        setFormWithSchemaId: action,
        setFavoriteFormIds: action.bound,
      },
      { autoBind: true },
    );
    // Les favoris de l'ancien stockage (dans la clé du store) sont mis de côté avant que la persistance ne
    // réécrive cette clé sans eux ; les favoris sont désormais conservés sous une clé par utilisateur
    const storageName = this.constructor.name;
    FormStore.moveLegacyFavorites(storageName);
    void makePersistable(this, {
      name: storageName,
      properties: ['formsWithSchemaId'],
      storage: window.localStorage,
    });

    this.MainMenuStore = new ViewStore<FormViewer>(this.logger, 'MainMenuStore');

    this.LeftMenuStore = new ViewStore<FormViewer>(this.logger, 'LeftMenuStore');

    // Favoris modifiés dans un autre onglet : la liste est reprise pour ne pas écraser ses changements
    window.addEventListener('storage', this.onFavoritesStorageChange);
  }

  setFormWithSchemaId = (newForms: FormViewer[] | undefined) => (this.formsWithSchemaId = newForms);

  setFavoriteFormIds = (favoriteFormIds: string[]) => (this.favoriteFormIds = favoriteFormIds);

  /** Indique si un formulaire est dans les favoris de l'utilisateur connecté */
  isFavoriteForm = (formId: string | undefined): boolean => {
    const id = normalizeFormId(formId);
    return id !== '' && this.favoriteFormIds.includes(id);
  };

  setShouldAbortRequests = (shouldAbortRequests: boolean) => (this.shouldAbortRequests = shouldAbortRequests);

  resetStore = (): void => {
    this.shouldAbortRequests = false;
    this.formsWithSchemaId = undefined;
    this.formsListByOuCache.clear();
    this.resetFavorites(undefined);
    this.MainMenuStore.reset();
    this.LeftMenuStore.reset();
  };

  public getFormsList = async (t: TFunction, isFavoritesFeatureEnabled = false): Promise<void> => {
    const {
      LoginStore: {
        signInInfos: { userUPN: forThisUPN },
        currentOuCode,
      },
      DeclarationStore: { synchronizationStatus, setSynchronizationStatus, setSynchronizationError },
    } = this.RootStore;

    try {
      if (!this.shouldAbortRequests) {
        // Arbre construit tout de suite avec les favoris conservés dans le navigateur : ils restent affichés
        // si le serveur ne répond pas (hors connexion)
        if (isFavoritesFeatureEnabled && this.ensureFavoritesUser()) {
          this.favoritesT = t;
          this.rebuildFavoritesTree();
        }

        // Favoris lus en même temps que les formulaires : l'arbre est construit une seule fois, avec ses favoris
        // (synchronizeFavorites ne rejette jamais)
        const favoritesSync = this.synchronizeFavorites(isFavoritesFeatureEnabled);

        // Mise a jour de l'url avec le userUPN enregistré

        const getFormViewerUri = buildApiUrl(API_GET_FORM_LIST, { forThisUPN });

        const headers: Record<string, string> = {};
        if (currentOuCode) {
          headers[OrganizationnalUnitCodeHeaderName] = currentOuCode;
        }
        const response = await this.clientHTTP.get<GetFormViewerResponse>(
          this.RootStore.CommonStore.chooseBaseUrl(getFormViewerUri),
          {
            headers,
            withCredentials: true,
            // 404 = utilisateur inconnu du référentiel QuickConnect: réponse métier attendue,
            // le client HTTP ne doit pas la tracer en erreur ni appliquer ses effets de bord.
            expectedStatuses: [404],
          },
        );

        const allForms = response.data.forms.map(mapBaseForm);
        const forms = allForms.filter((f) => !f.entitySchemaId);
        const orderedForms = forms.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        this.MainMenuStore.setItems(orderedForms);
        this.LeftMenuStore.setItems(orderedForms);

        await favoritesSync;
        this.favoritesT = t;
        this.MainMenuStore.setItemsTree(
          (f) => f.name,
          t,
          false,
          isFavoritesFeatureEnabled ? [...this.favoriteFormIds] : [],
        );

        const formsWithEntityId = allForms.filter((f) => f.entitySchemaId);
        const orderedFormsWithEntityId = formsWithEntityId.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
        this.setFormWithSchemaId(orderedFormsWithEntityId);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<GetFormViewerResponse>;

        // 404 renvoyé par la procédure stockée [QC].[GetFormViewer] : utilisateur inconnu
        // (MSG_SQL_USERNOTFOUND) ou unité organisationnelle invalide pour cet utilisateur
        // (MSG_SQL_OU_NOT_FOUND / MSG_SQL_USEROUNOTFOUND), typiquement un QC-OUCode périmé
        // conservé en storage après un changement de rattachement. On trace en avertissement
        // avec le code d'erreur et l'UO envoyée pour pouvoir discriminer les deux cas.
        if (errorAxios.response?.status === 404) {
          this.logger.warn(
            FormStore.TAG,
            `getFormsList: 404 sur getformviewer pour l'utilisateur "${forThisUPN}" - UO envoyée: "${
              currentOuCode || '(aucune)'
            }" - Code: ${errorAxios.response?.data?.errorCode ?? ''} - Message: ${
              errorAxios.response?.data?.message ?? ''
            }`,
          );

          return;
        }

        errorAxios.message = `Une erreur est survenue lors de la récupération des formulaires pour l'utilisateur "${forThisUPN}": Statut: ${errorAxios.response?.data.status}, Message: ${error.response?.data.message}`;
        errorHandler(FormStore.TAG, errorAxios, 'getFormsList');
        if (error.code === 'ERR_NETWORK' && synchronizationStatus !== DeclarationSynchronizationStatus.NotStarted) {
          setSynchronizationStatus(DeclarationSynchronizationStatus.Failed);
          setSynchronizationError(t('qcapp_offline_notice', { ns: 'declaration' }).toString());
          toast.warn(t('qcapp_offline_notice', { ns: 'declaration' }));
        }
      } else if (error instanceof Error) {
        error.message = `Une erreur est survenue lors de la récupération des formulaires pour l'utilisateur ${forThisUPN}: ${error.message}`;

        errorHandler(FormStore.TAG, error, 'getFormsList');
      }
    }
  };

  /**
   * Liste des formulaires visibles depuis une UO donnée, sans modifier les stores (null en cas d'échec).
   * Mise en cache par UO : les appels simultanés partagent la même requête, les échecs ne sont pas conservés.
   */
  public getFormsListForOu = (ouCode: string): Promise<FormViewer[] | null> => {
    const now = Date.now();
    const cached = this.formsListByOuCache.get(ouCode);
    if (cached && cached.expiresAt > now) return cached.forms;

    const forms = this.fetchFormsListForOu(ouCode).then((result) => {
      if (result === null) this.formsListByOuCache.delete(ouCode);
      return result;
    });
    this.formsListByOuCache.set(ouCode, { expiresAt: now + FormStore.FORMS_LIST_BY_OU_TTL_MS, forms });
    return forms;
  };

  private fetchFormsListForOu = async (ouCode: string): Promise<FormViewer[] | null> => {
    const {
      LoginStore: {
        signInInfos: { userUPN: forThisUPN },
      },
      CommonStore: { chooseBaseUrl },
    } = this.RootStore;

    try {
      const response = await this.clientHTTP.get<GetFormViewerResponse>(
        chooseBaseUrl(buildApiUrl(API_GET_FORM_LIST, { forThisUPN })),
        {
          headers: { [OrganizationnalUnitCodeHeaderName]: ouCode },
          withCredentials: true,
          expectedStatuses: [404],
        },
      );
      return response.data.forms.map(mapBaseForm);
    } catch {
      return null;
    }
  };

  // ------------------------------------------------------------------ Favoris

  /**
   * Ajoute ou retire un favori (clic sur l'étoile) : la liste courante est modifiée tout de suite, puis envoyée
   * au serveur avec la référence ; la liste fusionnée renvoyée remplace les deux.
   */
  public toggleFavorite = (formId: string, t: TFunction, isFavoritesFeatureEnabled: boolean): void => {
    if (!isFavoritesFeatureEnabled) return;
    const id = normalizeFormId(formId);
    if (id === '' || !this.ensureFavoritesUser()) return;

    this.favoritesT = t;
    this.setFavoriteFormIds(
      this.favoriteFormIds.includes(id)
        ? this.favoriteFormIds.filter((favoriteId) => favoriteId !== id)
        : [...this.favoriteFormIds, id],
    );
    this.saveFavorites();
    this.rebuildFavoritesTree();
    void this.synchronizeFavorites(isFavoritesFeatureEnabled, true);
  };

  /**
   * Synchronise les favoris avec le serveur (app/synchronizerepositoryforwebapp), un appel à la fois.
   * Sans changement en attente : lecture seule. Avec changements en attente (clic sur l'étoile, appel précédent
   * en échec, favoris de l'ancien stockage) : envoi de la liste courante et de la référence.
   * Ne rejette jamais : une erreur est tracée, les listes sont gardées (aussi dans le navigateur) et renvoyées à
   * l'appel suivant.
   *
   * @param fromUserAction true pour un clic sur l'étoile : un échec est signalé à l'utilisateur
   */
  public synchronizeFavorites = (isFavoritesFeatureEnabled: boolean, fromUserAction = false): Promise<void> => {
    if (!isFavoritesFeatureEnabled) return Promise.resolve();
    const run = this.favoritesQueue.then(() => this.synchronizeFavoritesNow(fromUserAction)).catch(() => undefined);
    this.favoritesQueue = run;
    return run;
  };

  private synchronizeFavoritesNow = async (fromUserAction: boolean): Promise<void> => {
    const userUPN = this.ensureFavoritesUser();
    if (!userUPN || this.shouldAbortRequests) return;

    const legacyFormIds = FormStore.getLegacyFavoriteFormIds(userUPN);
    const sent = normalizeFormIds([...this.favoriteFormIds, ...legacyFormIds]);
    const withChanges =
      legacyFormIds.length > 0 || hasPendingFavoriteChanges(this.favoriteFormIds, this.favoriteBaseFormIds);
    const request: SynchronizeRepositoryForWebAppRequest = withChanges
      ? { favoriteFormIds: sent, favoriteBaseFormIds: this.favoriteBaseFormIds ?? null }
      : { favoriteFormIds: null, favoriteBaseFormIds: null };

    try {
      const response = await this.clientHTTP.post<SynchronizeRepositoryForWebAppResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(API_SYNCHRONIZE_REPOSITORY_FOR_WEB_APP),
        request,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      this.favoritesSyncFailed = false;

      // Utilisateur changé pendant l'appel : la réponse ne le concerne pas
      if (this.favoritesUserUPN !== userUPN) return;

      const serverFormIds = response.data?.infos?.favoriteFormIds;
      if (!serverFormIds) {
        // Feature non souscrite ou fusion en échec côté serveur : listes conservées, renvoyées à l'appel suivant
        this.logger.info(FormStore.TAG, `synchronizeFavorites: pas de favoris renvoyés pour "${userUPN}"`);
        return;
      }

      this.applyServerFavorites(serverFormIds, withChanges ? sent : [...this.favoriteFormIds]);
      if (legacyFormIds.length > 0) {
        FormStore.removeLegacyFavorites(userUPN);
        this.logger.info(
          FormStore.TAG,
          `synchronizeFavorites: ${legacyFormIds.length} favori(s) de l'ancien stockage navigateur repris pour "${userUPN}"`,
        );
      }
    } catch (error) {
      this.logger.warn(
        FormStore.TAG,
        `synchronizeFavorites: échec de la synchro des favoris de "${userUPN}" (${
          withChanges ? 'envoi' : 'lecture'
        }) : ${error instanceof Error ? error.message : String(error)}`,
      );
      // Les favoris restent conservés dans le navigateur : un seul avertissement tant que les appels échouent
      // (hors connexion, chaque clic sur l'étoile échouerait)
      if (fromUserAction && !this.favoritesSyncFailed && this.favoritesT) {
        toast.warn(this.favoritesT('qcapp_favorites_sync_error', { ns: 'forms' }));
      }
      this.favoritesSyncFailed = true;
    }
  };

  /**
   * Applique la liste fusionnée du serveur : elle devient la référence, et la liste courante en garde les
   * changements faits depuis l'envoi de la requête (ils partiront à l'appel suivant)
   */
  private applyServerFavorites = (serverFormIds: string[], sent: string[]): void => {
    const server = normalizeFormIds(serverFormIds);
    const current = mergeServerFavorites(this.favoriteFormIds, sent, server);
    const changed = !sameFormIds(current, this.favoriteFormIds);
    this.favoriteBaseFormIds = server;
    if (changed) {
      this.setFavoriteFormIds(current);
    }
    this.saveFavorites();
    if (changed) {
      this.rebuildFavoritesTree();
    }
    // Changements faits pendant l'appel : l'étoile qui les a produits a déjà mis un appel en file
  };

  /** Reconstruit l'arbre des formulaires avec les favoris courants (si l'écran a déjà été construit) */
  private rebuildFavoritesTree = (): void => {
    if (this.favoritesT && this.MainMenuStore.items) {
      this.MainMenuStore.setItemsTree((f) => f.name, this.favoritesT, false, [...this.favoriteFormIds]);
    }
  };

  /**
   * Utilisateur connecté (UPN normalisé) ; s'il a changé, les listes de favoris sont remplacées par les siennes,
   * conservées dans le navigateur (vides s'il n'en a pas)
   * @returns undefined si aucun utilisateur n'est connecté
   */
  private ensureFavoritesUser = (): string | undefined => {
    const userUPN = normalizeUserUPN(this.RootStore.LoginStore.signInInfos?.userUPN) || undefined;
    if (userUPN !== this.favoritesUserUPN) {
      this.resetFavorites(userUPN);
    }
    return userUPN;
  };

  private resetFavorites = (userUPN: string | undefined): void => {
    this.favoritesSyncFailed = false;
    const stored = userUPN ? this.loadFavorites(userUPN) : undefined;
    this.favoritesUserUPN = userUPN;
    this.favoriteBaseFormIds = stored?.base;
    this.setFavoriteFormIds(stored?.current ?? []);
  };

  // ------------------------------------------------------------------ Favoris conservés dans le navigateur

  private static favoritesStorageKey = (userUPN: string): string => `${FormStore.FAVORITES_KEY_PREFIX}${userUPN}`;

  /** Favoris conservés dans le navigateur pour cet utilisateur (undefined si aucun ou illisibles) */
  private loadFavorites = (userUPN: string): StoredFavorites | undefined => {
    try {
      const raw = window.localStorage.getItem(FormStore.favoritesStorageKey(userUPN));
      const stored = parseStoredFavorites(raw);
      if (raw && !stored) {
        this.logger.warn(
          FormStore.TAG,
          `loadFavorites: favoris conservés illisibles pour "${userUPN}", relus du serveur`,
        );
      }
      return stored;
    } catch (error) {
      this.logger.warn(
        FormStore.TAG,
        `loadFavorites: lecture des favoris conservés impossible pour "${userUPN}" : ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
      return undefined;
    }
  };

  /** Conserve dans le navigateur la liste courante et la référence de l'utilisateur des favoris */
  private saveFavorites = (): void => {
    const userUPN = this.favoritesUserUPN;
    if (!userUPN) return;
    try {
      window.localStorage.setItem(
        FormStore.favoritesStorageKey(userUPN),
        serializeStoredFavorites({ current: [...this.favoriteFormIds], base: this.favoriteBaseFormIds }),
      );
    } catch (error) {
      // Stockage plein ou refusé : les favoris restent en mémoire et seront envoyés au serveur
      this.logger.warn(
        FormStore.TAG,
        `saveFavorites: enregistrement des favoris impossible pour "${userUPN}" : ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
  };

  /**
   * Favoris de l'utilisateur modifiés dans un autre onglet (l'événement storage n'est pas reçu par l'onglet qui
   * écrit) : les listes de cet onglet sont remplacées, pour qu'un clic ici n'efface pas un changement fait là-bas
   */
  private onFavoritesStorageChange = (event: StorageEvent): void => {
    const userUPN = this.favoritesUserUPN;
    if (!userUPN || event.key !== FormStore.favoritesStorageKey(userUPN)) return;
    if (!this.RootStore.LoginStore.checkForFeature(FEATURE_013)) return;

    const stored = parseStoredFavorites(event.newValue);
    if (!stored) return;

    this.favoriteBaseFormIds = stored.base;
    if (!sameFormIds(stored.current, this.favoriteFormIds)) {
      this.setFavoriteFormIds(stored.current);
      this.rebuildFavoritesTree();
    }
  };

  // ------------------------------------------------------------------ Reprise de l'ancien stockage navigateur

  /**
   * Met de côté les favoris de l'ancien stockage (propriété favoriteForms de la clé du store dans le
   * localStorage) avant que la persistance ne réécrive cette clé sans eux. Ils sont repris par le serveur à
   * la première synchro de chaque utilisateur (référence nulle : le serveur fait l'union).
   */
  private static moveLegacyFavorites = (storageName: string): void => {
    try {
      const raw = window.localStorage.getItem(storageName);
      if (!raw) return;
      const persisted = JSON.parse(raw);
      const entries: LegacyFavoriteFormEntry[] = Array.isArray(persisted?.favoriteForms) ? persisted.favoriteForms : [];
      if (persisted && 'favoriteForms' in persisted) {
        if (entries.length > 0) {
          FormStore.writeLegacyFavorites([...FormStore.readLegacyFavorites(), ...entries]);
        }
        delete persisted.favoriteForms;
        window.localStorage.setItem(storageName, JSON.stringify(persisted));
      }
    } catch {
      // Ancien stockage illisible : rien à reprendre
    }
  };

  private static readLegacyFavorites = (): LegacyFavoriteFormEntry[] => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(FormStore.LEGACY_FAVORITES_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  private static writeLegacyFavorites = (entries: LegacyFavoriteFormEntry[]): void => {
    if (entries.length === 0) {
      window.localStorage.removeItem(FormStore.LEGACY_FAVORITES_KEY);
    } else {
      window.localStorage.setItem(FormStore.LEGACY_FAVORITES_KEY, JSON.stringify(entries));
    }
  };

  /** Favoris de l'ancien stockage revenant à cet utilisateur (les plus anciens, sans UPN, reviennent au premier) */
  private static isLegacyFavoriteOf = (entry: LegacyFavoriteFormEntry, userUPN: string): boolean =>
    !entry?.userUPN || normalizeUserUPN(entry.userUPN) === userUPN;

  private static getLegacyFavoriteFormIds = (userUPN: string): string[] =>
    normalizeFormIds(
      FormStore.readLegacyFavorites()
        .filter((entry) => FormStore.isLegacyFavoriteOf(entry, userUPN))
        .map((entry) => entry?.id),
    );

  private static removeLegacyFavorites = (userUPN: string): void => {
    FormStore.writeLegacyFavorites(
      FormStore.readLegacyFavorites().filter((entry) => !FormStore.isLegacyFavoriteOf(entry, userUPN)),
    );
  };
}
export default FormStore;
